import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { EstadoDeComunicacion, IconosDeCanal } from './piezas';
import { POR_PAGINA, useDespachar, useHistorial, type Comunicacion } from './api';

/**
 * Historial.tsx — opción 2 del módulo 6: qué se ha enviado y qué espera salir.
 *
 * ## Carga por su cuenta, y eso es el arreglo principal
 *
 * El vanilla pedía esto y el catálogo de audiencias con un `Promise.all`, y
 * envolvía las dos en un `catch` que sustituía la pantalla entera. Comprobado
 * en el navegador el 2026-09-15 haciendo fallar **solo** esta petición:
 *
 *     compositor visible: false      ← no necesitaba el historial para nada
 *     historial visible:  false
 *     en pantalla:        «No pudimos cargar las comunicaciones»
 *
 * Es el caso que `Principios.md` documenta con Perfil: «nada de un Promise.all
 * que bloquee la pantalla entera».
 *
 * ## Dice cuántas está enseñando
 *
 * `GET /communications/` devuelve una lista, sin total. Cuando llegan
 * exactamente las 30 que se piden, lo honesto es decir que son las 30 más
 * recientes y que puede haber más — no dar a entender que eso es todo. Saber
 * el número exacto exigiría ampliar el endpoint, y eso es un cambio de
 * contrato: queda escrito en la ficha del módulo.
 */

/** La fecha de una comunicación: cuándo salió, o cuándo va a salir. */
function Cuando({ c }: { c: Comunicacion }) {
  if (c.status === 'programada' && c.scheduled_at) {
    return (
      <span className="inline-flex items-center gap-1">
        <i className="bi bi-clock" aria-hidden="true" />
        {/* `Utils.fmtDatetime` y no `scheduled_at.replace('T',' ').slice(0,16)`,
            que es lo que hacía el vanilla: eso enseña la marca del servidor sin
            convertirla a la zona local de quien la lee. */}
        Programada para {Utils.fmtDatetime(c.scheduled_at)}
      </span>
    );
  }
  if (c.sent_at) return <>Enviada el {Utils.fmtDatetime(c.sent_at)}</>;
  if (c.created_at) return <>Creada el {Utils.fmtDatetime(c.created_at)}</>;
  return null;
}

function Fila({ c }: { c: Comunicacion }) {
  const despachar = useDespachar();
  const [confirmando, setConfirmando] = useState(false);
  const sePuedeEnviarYa = c.status === 'borrador' || c.status === 'programada';
  const m = c.metrics ?? {};

  return (
    <Card className="p-3">
      <div className="flex items-start gap-2">
        <h3 className="min-w-0 flex-1 text-3 font-semibold text-brand-ink">{c.title}</h3>
        <EstadoDeComunicacion estado={c.status} />
      </div>

      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-2 text-muted">
        <IconosDeCanal canales={c.channels} />
        <span>·</span>
        <span>{c.audience_label}</span>
        <span>·</span>
        <span>
          {c.recipient_count} destinatario{c.recipient_count === 1 ? '' : 's'}
        </span>
      </p>

      <p className="mt-0.5 text-2 text-muted">
        <Cuando c={c} />
      </p>

      {c.status === 'enviada' && (
        <p className="mt-1 text-2 text-ok-ink">
          <span aria-hidden="true">
            <i className="bi bi-envelope" /> {m.emailed ?? 0} ·{' '}
            <i className="bi bi-megaphone" /> {m.feed ?? 0} ·{' '}
            <i className="bi bi-bell" /> {m.notif ?? 0}
          </span>
          <span className="sr-only">
            {m.emailed ?? 0} por correo, {m.feed ?? 0} en el muro, {m.notif ?? 0} en el buzón
          </span>
        </p>
      )}

      {sePuedeEnviarYa && (
        <div className="mt-2 flex justify-end">
          <Button size="sm" loading={despachar.isPending} onClick={() => setConfirmando(true)}>
            <i className="bi bi-send" aria-hidden="true" /> Enviar ahora
          </Button>
        </div>
      )}

      <ConfirmDialog
        abierto={confirmando}
        onCambio={setConfirmando}
        titulo="Enviar ahora"
        descripcion={
          `«${c.title}» saldrá inmediatamente a ${c.recipient_count} ` +
          `destinatario${c.recipient_count === 1 ? '' : 's'}` +
          (c.status === 'programada' ? ', antes de su hora programada.' : '.') +
          (c.channels.includes('email') ? ' El correo no se puede retirar una vez enviado.' : '')
        }
        textoConfirmar="Enviar ahora"
        cargando={despachar.isPending}
        onConfirmar={() =>
          despachar.mutate(c.id, {
            onSuccess: () => {
              setConfirmando(false);
              Utils.toast('En envío', 'success');
            },
            onError: (e: unknown) => {
              setConfirmando(false);
              Utils.toast(e instanceof Error ? e.message : 'No se pudo enviar', 'error');
            },
          })
        }
      />
    </Card>
  );
}

export function Historial() {
  const { data, isLoading, isError, error, refetch } = useHistorial();

  return (
    <section aria-labelledby="titulo-historial">
      <h2
        id="titulo-historial"
        className="mb-2.5 flex items-center gap-2 text-3 font-bold text-brand-ink"
      >
        <i className="bi bi-clock-history" aria-hidden="true" /> Historial
      </h2>

      {isLoading ? (
        <div className="flex flex-col gap-2.5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : isError ? (
        // El error se queda AQUÍ. El compositor de al lado sigue funcionando:
        // no necesita el historial para escribir una comunicación.
        <Card className="p-4">
          <EstadoError err={error} que="el historial" onReintentar={() => refetch()} />
        </Card>
      ) : !data?.length ? (
        <EstadoVacio
          icono="bi-megaphone"
          titulo="Sin comunicaciones"
          descripcion="Las que envíes aparecerán aquí, con su estado y su alcance."
          compacto
        />
      ) : (
        <>
          <div className="flex flex-col gap-2.5">
            {data.map((c) => (
              <Fila key={c.id} c={c} />
            ))}
          </div>
          {data.length >= POR_PAGINA && (
            <p className="mt-3 text-2 text-muted">
              Mostrando las {POR_PAGINA} más recientes.
            </p>
          )}
        </>
      )}
    </section>
  );
}
